import { createColumnHelper } from '@tanstack/react-table';
import _ from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
	DataTableSelectorCheckbox,
	DataTableSelectorMenu,
	HeaderCell,
	TruncatedTextCell,
} from '@adflow/frontend/ui-data-table';
import { theme } from '@adflow/frontend/ui-theme';
import {
	formatDateAndTime,
	formatDateOrTime,
} from '@adflow/shared/util-formatting';
import type { ViewGMBTemplateDtoWithUploadResult } from '@adflow/shared/util-history';

import type { UploadResultTag } from '../../GoogleAds/GoogleAdsTemplates/UploadResultTagsCell';
import { UploadResultTagsCell } from '../../GoogleAds/GoogleAdsTemplates/UploadResultTagsCell';

const columnHelper = createColumnHelper<ViewGMBTemplateDtoWithUploadResult>();

export function useGMBTemplateColumns({ onDelete }: { onDelete?: () => void }) {
	const { t } = useTranslation(['common', 'facebook', 'google-ads']);

	return useMemo(
		() => [
			columnHelper.display({
				id: 'templateId',
				meta: {
					name: 'Selector',
					disableOrdering: true,
					disableConfig: true,
					disableOverflow: true,
					sticky: true,
				},
				size: 48,
				enableSorting: false,
				enableResizing: false,

				header: ({ table }) => (
					<DataTableSelectorMenu
						items={table.getCenterRows().map((row) => row.original)}
						getId={(item) => item.id}
						options={[
							{
								content: t('common:buttons.delete'),
								onSelect: () => onDelete?.(),
								key: 'delete-selection',

								isVisible: true,
								isLink: true,
								testid: 'delete-selection',
								style: { color: theme.colors.palettes.delete.main },
							},
							{
								content: t('common:buttons.clearSelection'),
								onSelect: ({ setSelected }) => {
									setSelected([]);
								},
								key: 'clear-selection',

								isVisible: true,
								isLink: true,
								testid: 'clear-selection',
								style: { color: theme.colors.palettes.delete.main },
							},
						]}
					/>
				),
				cell: ({ row }) => {
					return (
						<DataTableSelectorCheckbox
							item={row.original}
							getId={(item) => item.id}
						/>
					);
				},
			}),
			columnHelper.accessor('name', {
				id: 'name',
				meta: {
					name: t('facebook:template.columns.name'),
					sticky: true,
				},

				header: HeaderCell,
				cell: ({ getValue, row }) => (
					<TruncatedTextCell
						value={getValue()}
						archived={row.original.archived}
					/>
				),
			}),
			columnHelper.accessor('createdAt', {
				id: 'createdAt',
				meta: {
					name: t('facebook:template.columns.createdAt'),
				},
				sortDescFirst: true,

				header: ({ column }) => <HeaderCell align="right" column={column} />,
				cell: ({ getValue, row }) => {
					const value = getValue();
					return (
						<TruncatedTextCell
							align="right"
							value={value ? formatDateOrTime(value) : null}
							archived={row.original.archived}
						/>
					);
				},
			}),
			columnHelper.accessor('updatedAt', {
				id: 'updatedAt',
				meta: {
					name: t('facebook:template.columns.updatedAt'),
				},
				sortDescFirst: true,

				header: ({ column }) => <HeaderCell align="right" column={column} />,
				cell: ({ getValue, row }) => {
					const value = getValue();
					return (
						<TruncatedTextCell
							align="right"
							value={value ? formatDateOrTime(value) : null}
							archived={row.original.archived}
						/>
					);
				},
			}),
			columnHelper.accessor('scheduledPublishAt', {
				id: 'scheduledPublishAt',
				meta: {
					name: t('google-ads:schedulePublish'),
				},
				enableSorting: false,

				header: ({ column }) => <HeaderCell align="right" column={column} />,
				cell: ({ getValue, row }) => {
					const value = getValue();
					return (
						<TruncatedTextCell
							align="right"
							value={value ? formatDateAndTime(value) : null}
							archived={row.original.archived}
						/>
					);
				},
			}),
			columnHelper.accessor('uploadResult', {
				id: 'lastUploaded',
				meta: {
					name: t('facebook:history.columns.lastUploaded'),
				},
				enableSorting: false,
				size: 80,

				header: ({ column }) => <HeaderCell align="right" column={column} />,
				cell: ({ getValue, row }) => {
					const value = getValue();

					const uploadTime = value?.newData?.finishedAt;

					return (
						<TruncatedTextCell
							align="right"
							value={uploadTime ? formatDateOrTime(uploadTime) : null}
							archived={row.original.archived}
						/>
					);
				},
			}),
			columnHelper.accessor('uploadResult', {
				id: 'uploadResult',
				meta: {
					name: t('facebook:history.columns.uploadResult'),
				},
				enableSorting: false,
				size: 200,

				header: ({ column }) => <HeaderCell column={column} />,
				cell: ({ getValue }) => {
					const value = getValue();

					if (!value?.newData?.results) {
						return null;
					}

					const { results } = value.newData;

					const tags: UploadResultTag[] = results.map((result) => {
						const [, name, address] = result.location.split(' :: ');

						return {
							avbId:
								result.avbId ||
								_.truncate(name, { length: 5 }) ||
								_.truncate(address, { length: 5 }),
							type:
								result.status === 'SUCCESS'
									? ('success' as const)
									: ('error' as const),
							errors: result.status === 'SUCCESS' ? [] : result.errors,
						};
					});

					return <UploadResultTagsCell tags={tags} />;
				},
			}),
			columnHelper.accessor('imported', {
				id: 'imported',
				meta: {
					name: t('history.columns.origin'),
				},
				size: 100,
				header: HeaderCell,
				cell: ({ getValue }) => {
					const imported = getValue();

					const origin = imported ? 'Magento' : 'User';

					return <TruncatedTextCell value={origin} />;
				},
			}),
		],
		[onDelete, t]
	);
}
